import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, message, Button, Row, Col, Alert, Upload } from 'antd';
import type { User, CreateUserInput, UpdateUserInput, UserRole, UserStatus } from '../../../types/users';
import type { Contact, WorkspaceReference, ActivityRecord } from '../../../types/common';
import { addUser, updateUser } from '../../../api/users';
import { getWorkspaces } from '../../../api/workspaces';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserOutlined, MailOutlined, LockOutlined, UploadOutlined, PhoneOutlined } from '@ant-design/icons';
import CurrencySelector from '../../common/CurrencySelector';
import LanguageSelector from '../../common/LanguageSelector';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebase';
import { useAuth } from '../../../contexts/AuthContext';

const staticRoleOptions = [
  { value: 'Root user', label: 'Root user' },
  { value: 'administrator', label: 'Administrator' },
  { value: 'employee', label: 'Employee' },
  { value: 'resident', label: 'Resident' },
  { value: 'vendor', label: 'Vendor' },
];

interface UserModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  user?: User;
}

interface UserFormValues {
  email: string;
  password: string;
  displayName: string;
  username: string;
  status: UserStatus;
  contacts: Contact[];
  roles: UserRole[];
  workspace: WorkspaceReference;
  workspaceId?: string;
  language: 'en' | 'es' | 'pt';
  currency: 'USD' | 'CLP' | 'BRL';
  profilePicture?: FileMetadata;
  phoneNumber?: string;
}

interface FileMetadata {
  name: string;
  type: string;
  size: number;
  path: string;
  url: string;
}

// Helper function to generate username from display name
const generateUsername = (displayName: string): string => {
  return displayName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9]/g, '-') // Replace non-alphanumeric with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
    .substring(0, 30); // Limit length
};

const UserModal: React.FC<UserModalProps> = ({ visible, onCancel, onSuccess, user }) => {
  const [form] = Form.useForm<UserFormValues>();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [profilePictureMetadata, setProfilePictureMetadata] = useState<FileMetadata | undefined>(undefined);
  const { user: currentUser } = useAuth();

  const addUserMutation = useMutation({
    mutationFn: (data: CreateUserInput) => addUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success('User created successfully');
      form.resetFields();
      setError(null);
      onSuccess();
    },
    onError: (error: Error) => {
      if (error.message === 'USER_EXISTS') {
        setError('This user already exists in our system.');
      } else {
        setError(error.message || 'Failed to create user');
      }
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserInput }) => updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success('User updated successfully');
      form.resetFields();
      onSuccess();
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to update user');
    },
  });

  useEffect(() => {
    if (visible) {
      form.resetFields();
      if (user) {
        form.setFieldsValue({
          ...user,
          roles: user.roles || [],
          profilePicture: user.profilePicture ? {
            name: user.profilePicture.name,
            type: user.profilePicture.type,
            size: user.profilePicture.size,
            path: user.profilePicture.path,
            url: user.profilePicture.url
          } : undefined
        });
      } else {
        setWorkspaces([])
      }
      // Fetch all workspaces for workspace selection
      getWorkspaces().then(res => setWorkspaces(res.workspaces || []));
    }
  }, [visible, user, form]);

  const handleWorkspaceChange = (value: string) => {
    console.log('Workspace change triggered with value:', value);
    const selectedWorkspace = workspaces.find(w => w.id === value);
    console.log('Selected workspace:', selectedWorkspace);
    
    if (selectedWorkspace) {
      const workspaceData = {
        id: selectedWorkspace.id,
        name: selectedWorkspace.name,
        slug: selectedWorkspace.slug
      };
      console.log('Setting workspace data:', workspaceData);
      
      // Set the complete workspace object with all required fields
      form.setFieldsValue({
        workspace: workspaceData,
        language: selectedWorkspace.language,
        currency: selectedWorkspace.currency
      });
      
      // Verify the data was set correctly
      setTimeout(() => {
        const currentWorkspace = form.getFieldValue('workspace');
        console.log('Workspace data after setting:', currentWorkspace);
      }, 100);
    }
  };

  // Handle display name change to generate username
  const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const displayName = e.target.value;
    if (displayName && !user) { // Only generate for new users
      const username = generateUsername(displayName);
      form.setFieldsValue({ username });
    }
  };

  const handleProfilePictureUpload = async (file: File) => {
    try {
      // Get workspace info and username from form
      const workspace = form.getFieldValue('workspace');
      const username = form.getFieldValue('username');

      if (!workspace?.id || !workspace?.slug || !username) {
        message.error('Please select a workspace first');
        return false;
      }

      const storagePath = `workspaces/${workspace.slug}/users/${username}/profile-pictures/${file.name}`;
      const storageRef = ref(storage, storagePath);
      
      // Upload file
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Create metadata object that matches FileMetadata interface
      const metadata: FileMetadata = {
        name: file.name,
        type: file.type,
        size: file.size,
        path: storagePath,
        url: downloadURL
      };

      // Store the metadata in state
      setProfilePictureMetadata(metadata);
      
      message.success('Profile picture uploaded successfully');
      return false; // Prevent default upload behavior
    } catch (error: any) {
      console.error('Upload error:', error);
      message.error(error.message || 'Failed to upload profile picture');
      return false;
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if current user is authenticated
      if (!currentUser) {
        message.error('You must be authenticated to create users');
        return;
      }

      // Get the current form values
      const currentValues = form.getFieldsValue();
      
      // Debug: Log the workspace data
      console.log('Form workspace data:', currentValues.workspace);
      console.log('All form values:', currentValues);
      
      // Ensure workspace has all required fields
      const workspace = currentValues.workspace;
      if (!workspace || !workspace.id || !workspace.name || !workspace.slug) {
        console.error('Missing workspace fields:', workspace);
        message.error('Please select a workspace with complete information');
        return;
      }

      // Create activity record for the current user performing the action
      const activityRecord: ActivityRecord = {
        timestamp: new Date(),
        type: user ? 'updated' : 'created',
        performedBy: {
          id: currentUser.uid,
          displayName: currentUser.displayName || currentUser.email || 'Unknown User'
        },
        details: {
          field: 'user',
          oldValue: user ? {
            displayName: user.displayName,
            status: user.status,
            roles: user.roles
          } : undefined,
          newValue: {
            displayName: currentValues.displayName,
            status: currentValues.status,
            roles: currentValues.roles,
            workspace: workspace
          }
        }
      };

      // Remove the workspaceId field as it's not needed in the final data
      const { profilePicture, workspaceId, ...restValues } = currentValues;

      if (user) {
        // Update existing user
        const updateData: UpdateUserInput = {
          ...restValues,
          profilePicture: profilePictureMetadata,
          activityRecords: [activityRecord]
        };

        await updateUserMutation.mutateAsync({
          id: user.id,
          data: updateData
        });
      } else {
        // Create new user
        const createData: CreateUserInput = {
          ...restValues,
          profilePicture: profilePictureMetadata,
          activityRecords: [activityRecord]
        };

        console.log('Creating user with data:', createData);
        await addUserMutation.mutateAsync(createData);
      }

      message.success(user ? 'User updated successfully' : 'User created successfully');
      onCancel();
    } catch (error: any) {
      console.error('Form submission error:', error);
      message.error(error.message || 'Failed to submit form');
    } finally {
      setLoading(false);
    }
  };

  // Reset profile picture metadata when modal is opened for a new user
  useEffect(() => {
    if (!user) {
      setProfilePictureMetadata(undefined);
    }
  }, [user]);

  const handleCancel = () => {
    form.resetFields();
    setError(null);
    onCancel();
  };

  return (
    <Modal
      title={user ? 'Edit User' : 'Add User'}
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={() => form.submit()}
        >
          {user ? 'Update' : 'Create'}
        </Button>
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={user ? {
          ...user,
          profilePicture: user.profilePicture ? {
            name: user.profilePicture.name,
            type: user.profilePicture.type,
            size: user.profilePicture.size,
            path: user.profilePicture.path,
            url: user.profilePicture.url
          } : undefined
        } : undefined}
      >
        {!user && (
          <>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please input email!' },
                { type: 'email', message: 'Please enter a valid email!' },
              ]}
            >
              <Input prefix={<MailOutlined />} />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please input password!' },
                { min: 6, message: 'Password must be at least 6 characters!' },
              ]}
            >
              <Input.Password prefix={<LockOutlined />} />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="displayName"
          label="Display Name"
          rules={[{ required: true, message: 'Please input display name!' }]}
        >
          <Input prefix={<UserOutlined />} onChange={handleDisplayNameChange} />
        </Form.Item>

        <Form.Item
          name="phoneNumber"
          label="Phone Number"
          rules={[
            { pattern: /^\+?[\d\s-]+$/, message: 'Please enter a valid phone number!' }
          ]}
        >
          <Input prefix={<PhoneOutlined />} />
        </Form.Item>

        {!user && (
          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: 'Username is required!' }]}
            hidden // Hide from UI but keep in form
          >
            <Input />
          </Form.Item>
        )}

        <Form.Item
          name="status"
          label="Status"
          rules={[{ required: true, message: 'Please select status!' }]}
        >
          <Select>
            <Select.Option value="active">Active</Select.Option>
            <Select.Option value="inactive">Inactive</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="workspaceId"
          label="Workspace"
          rules={[{ required: true, message: 'Please select a workspace!' }]}
        >
          <Select
            placeholder="Select workspace"
            showSearch
            optionFilterProp="children"
            onChange={handleWorkspaceChange}
          >
            {workspaces.map(ws => (
              <Select.Option key={ws.id} value={ws.id}>{ws.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Hidden field to store the complete workspace object */}
        <Form.Item name="workspace" hidden>
          <Input />
        </Form.Item>

        <Form.Item
          name="roles"
          label="Roles"
          rules={[{ required: true, message: 'Please select at least one role!' }]}
        >
          <Select
            mode="multiple"
            placeholder="Select roles"
            options={staticRoleOptions}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="language"
              label="Language"
            >
              <LanguageSelector />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="currency"
              label="Currency"
            >
              <CurrencySelector />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Profile Picture"
          name="profilePicture"
          tooltip="Select a workspace first to upload a profile picture"
        >
          <Upload
            name="profilePicture"
            listType="picture"
            maxCount={1}
            beforeUpload={handleProfilePictureUpload}
            onRemove={() => {
              setProfilePictureMetadata(undefined);
            }}
          >
            <Button icon={<UploadOutlined />}>
              Choose File
            </Button>
          </Upload>
        </Form.Item>
      </Form>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}
    </Modal>
  );
};

export default UserModal; 